// Styles
import { FontSizes, FontWeights, memoizeFunction } from "@fluentui/react";
import { BoardDisplayMode } from "@ms/uno-constants/lib/local/BoardDisplayMode";
import { numberToPx, pxToNumber } from "@ms/uno-utilities/lib/local/StyleUtilities";
import { ScrollbarSpaceAround, ShadowMargin } from "../../../../constants/StyleConstants";
import { flexChildSizeFixed, flexChildSizeFlexible, flexParent, fontColorNeutralPrimary, styleButtonAsDiv, textClip } from "../../../../utilities/Mixin.styles";
import { isLoopTheme } from "../../../../utilities/ThemingUtilities";
// Variables shared within boardView
export const boardColumnAddButtonHeight = "40px";
export const boardColumnAddButtonHeightLoop = "44px";
export const boardColumnHorizontalPadding = "15px";
export const boardColumnHorizontalPaddingLoop = "12px";
export const boardColumnHorizontalPaddingTeams = "30px";
export const boardColumnTitleHeight = "32px";
export const columnInternalHorizontalPadding = ShadowMargin;
export const halfCardGap = "4px";
export const halfColumnHeaderIconTextSpacing = "5px";
export const taskCardContentWidth = "288px";
export const taskCardContentWidthLoop = "260px";
/**
 * Helper to get styles for a board column
 * @param theme Planner theme
 * @param displayMode "Board" or "List" display mode
 * @param isSlimView true if slim mode is enabled due to high zoom or low screen width
 */ export const getBoardColumnStyles = memoizeFunction((theme, displayMode, isSlimView)=>{
    const boardPadding = isLoopTheme(theme) ? `0 ${boardColumnHorizontalPaddingLoop}` : `0 ${boardColumnHorizontalPadding}`;
    return [
        flexParent("column"),
        displayMode === BoardDisplayMode.Board && [
            {
                padding: boardPadding
            }
        ],
        displayMode === BoardDisplayMode.List && {
            display: "table"
        },
        {
            // This gives the appearance of margin with the behavior of padding, in order to prevent a dead-zone between columns from impacting drag and drop behavior
            boxSizing: "contentBox",
            "> .columnContent": [
                flexParent("column"),
                flexChildSizeFlexible("100%"),
                {
                    marginLeft: `-${columnInternalHorizontalPadding}`,
                    marginRight: `-${columnInternalHorizontalPadding}`
                }
            ]
        },
        // [Accessibility fix] Enable the scroll bar page is in slim view (zoomed in >~300% or mobile view).
        isSlimView && {
            ".columnContent": [
                {
                    overflowX: "hidden",
                    overflowY: "auto"
                }
            ]
        }
    ];
});
/**
 * Helper to get styles for a board column header
 */ export const getBoardColumnHeaderStyles = memoizeFunction((theme)=>{
    const contentWidth = isLoopTheme(theme) ? taskCardContentWidthLoop : taskCardContentWidth;
    return {
        paddingLeft: columnInternalHorizontalPadding,
        paddingRight: columnInternalHorizontalPadding,
        "> .columnHeaderTitle": [
            flexChildSizeFixed(),
            flexParent(),
            {
                width: contentWidth
            }
        ]
    };
});
/**
 * Helper to get styles for a board title section
 * @param theme Planner theme
 * @param isReadOnly true if in readonly mode
 */ export const getBoardTitleSectionStyles = memoizeFunction((theme, isReadOnly)=>{
    const isLoop = isLoopTheme(theme);
    return [
        flexChildSizeFlexible(),
        flexParent(),
        textClip(),
        fontColorNeutralPrimary(theme),
        {
            height: boardColumnTitleHeight,
            lineHeight: boardColumnTitleHeight,
            margin: `0px -${halfColumnHeaderIconTextSpacing}`,
            fontSize: FontSizes.medium,
            fontWeight: FontWeights.semibold,
            "> *": {
                margin: `0px ${halfColumnHeaderIconTextSpacing}`
            },
            "> .columnTitle": [
                flexChildSizeFlexible(),
                flexParent(),
                {
                    cursor: isReadOnly ? "default" : "pointer",
                    ".plannerIcon": {
                        display: "none"
                    },
                    "> h3": [
                        styleButtonAsDiv,
                        flexChildSizeFlexible(),
                        textClip()
                    ]
                }
            ],
            "> .editColumnTextField": [
                flexChildSizeFlexible(),
                {
                    ".ms-TextField-field": {
                        paddingLeft: "10px"
                    }
                }
            ]
        },
        isLoop && {
            paddingTop: ShadowMargin
        }
    ];
});
export const columnContentWidth = numberToPx(pxToNumber(taskCardContentWidth) + pxToNumber(ScrollbarSpaceAround));
/**
 * Returns the color of placeholder column/card by Theme
 * @param theme Current theme
 */ export const getPlaceholderColor = (theme)=>{
    return theme.palette.neutralLight;
};
